import React, { useEffect, useRef, useState } from 'react';
import { ACCEPTED_MEDIA, isVideo } from '../../lib/media';
import Lightbox from './Lightbox';

// Deleting is two taps rather than one, and the confirm takes the focus so a
// keyboard never lands on nothing when the remove button disappears.
const ConfirmDelete = ({ onConfirm, onCancel }) => {
  const confirmRef = useRef(null);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  return (
    <div className="helen-media-confirm">
      <button ref={confirmRef} type="button" className="helen-media-yes" onClick={onConfirm}>
        Delete
      </button>
      <button type="button" className="helen-media-no" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
};

const MediaStrip = ({ entry, mediaUrls, onUpload, onDelete }) => {
  const media = entry.photos || [];
  const [openPath, setOpenPath] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const triggerRef = useRef(null);

  const open = (item, element) => {
    triggerRef.current = element;
    setOpenPath(item.storage_path);
  };

  const close = () => {
    setOpenPath(null);
    triggerRef.current?.focus();
  };

  const pick = (event) => {
    const [file] = event.target.files || [];
    if (file) onUpload(file);
    event.target.value = '';
  };

  const remove = (item) => {
    setConfirming(null);
    onDelete(item);
  };

  const altFor = (item) => item.caption || `Photo from ${entry.title}`;
  const opened = media.find((item) => item.storage_path === openPath) || null;

  return (
    <div className="helen-media">
      {media.map((item) => {
        const url = mediaUrls[item.storage_path];
        const alt = altFor(item);
        const video = isVideo(item.storage_path);

        return (
          <div key={item.id} className="helen-media-slot">
            {!url ? (
              <span className="helen-media-item is-loading" aria-hidden="true" />
            ) : video ? (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                className="helen-media-item is-video"
                src={url}
                controls
                playsInline
                preload="metadata"
              />
            ) : (
              <button
                type="button"
                className="helen-media-button"
                aria-label={`Open ${alt.charAt(0).toLowerCase()}${alt.slice(1)}`}
                onClick={(event) => open(item, event.currentTarget)}
              >
                <img className="helen-media-item" src={url} alt={alt} loading="lazy" />
              </button>
            )}

            {confirming === item.storage_path ? (
              <ConfirmDelete onConfirm={() => remove(item)} onCancel={() => setConfirming(null)} />
            ) : (
              <button
                type="button"
                className="helen-media-remove"
                aria-label={`Remove ${video ? 'clip' : 'photo'} from ${entry.title}`}
                onClick={() => setConfirming(item.storage_path)}
              >
                <span aria-hidden="true">×</span>
              </button>
            )}
          </div>
        );
      })}

      <label className="helen-media-add" htmlFor={`media-${entry.id}`}>
        <span>Add a photo or video</span>
        <input
          id={`media-${entry.id}`}
          type="file"
          accept={ACCEPTED_MEDIA}
          className="helen-visually-hidden"
          onChange={pick}
        />
      </label>

      {opened && (
        <Lightbox src={mediaUrls[opened.storage_path]} alt={altFor(opened)} onClose={close} />
      )}
    </div>
  );
};

export default MediaStrip;
